import { useState } from "react";
import { DateRangePicker, SelectPicker } from "rsuite";
import { formatDateRange } from "../../utils/utils";
import searchIcon from "../../assets/icons/search-icon.svg";
import filterIcon from "../../assets/icons/filter-icon.svg";
import downloadIcon from "../../assets/icons/download-icon.svg";
import "./BookingsTableFilters.css";

const BookingsTableFilters = ({
  onSearch,
  onDownload,
  onDateRangeApply,
  onFilterChange,
  searchValue,
  onViewToggle,
  viewToggleLabel,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [dateRanges, setDateRanges] = useState({
    checkIn: { start: null, end: null },
    payment: { start: null, end: null },
  });
  const [bookingStatus, setBookingStatus] = useState("");
  const [bookingType, setBookingType] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [transactionStatus, setTransactionStatus] = useState("");

  const handleFilterToggle = () => {
    setShowFilters(!showFilters);
  };

  const handleDateRangeChange = (filterType, value) => {
    // Handle null, undefined, or empty array
    if (!value || !Array.isArray(value) || value.length !== 2) {
      setDateRanges((prev) => ({
        ...prev,
        [filterType]: { start: null, end: null },
      }));
      if (onDateRangeApply) {
        onDateRangeApply(filterType, null, null);
      }
      return;
    }

    // Check if both dates are valid Date objects
    const [startDateObj, endDateObj] = value;
    if (
      !startDateObj ||
      !endDateObj ||
      !(startDateObj instanceof Date) ||
      !(endDateObj instanceof Date)
    ) {
      return;
    }

    // Validate dates are not invalid
    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
      return;
    }

    try {
      // Format dates using local timezone to avoid timezone shifts
      const formatDateToString = (dateObj) => {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const startDate = formatDateToString(startDateObj);
      const endDate = formatDateToString(endDateObj);

      setDateRanges((prev) => ({
        ...prev,
        [filterType]: { start: startDate, end: endDate },
      }));

      if (onDateRangeApply) {
        onDateRangeApply(filterType, startDate, endDate);
      }
    } catch (error) {
      console.error("Error converting date:", error);
    }
  };

  const getDateRangeValue = (filterType) => {
    const range = dateRanges[filterType];
    if (range?.start && range?.end) {
      try {
        // Parse date string (YYYY-MM-DD format)
        const startDateStr = range.start;
        const endDateStr = range.end;

        // Create date objects - use local time to avoid timezone issues
        const startDate = new Date(startDateStr + "T00:00:00");
        const endDate = new Date(endDateStr + "T00:00:00");

        // Validate dates
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.warn(`Invalid date range for ${filterType}:`, range);
          return null;
        }

        return [startDate, endDate];
      } catch (error) {
        console.error("Error parsing date:", error, "for range:", range);
        return null;
      }
    }
    return null;
  };

  const handleBookingStatusChange = (value) => {
    setBookingStatus(value || "");
    if (onFilterChange) {
      onFilterChange({ bookingStatus: value || "" });
    }
  };

  const handleBookingTypeChange = (value) => {
    setBookingType(value || "");
    if (onFilterChange) {
      onFilterChange({ bookingType: value || "" });
    }
  };

  const handleTransactionTypeChange = (value) => {
    setTransactionType(value || "");
    if (onFilterChange) {
      onFilterChange({ transactionType: value || "" });
    }
  };

  const handleTransactionStatusChange = (value) => {
    setTransactionStatus(value || "");
    if (onFilterChange) {
      onFilterChange({ transactionStatus: value || "" });
    }
  };

  const bookingStatusOptions = [
    { label: "Confirmed", value: "confirmed" },
    { label: "Cancelled", value: "cancelled" },
  ];

  const bookingTypeOptions = [
    { label: "Refundable", value: "REFUNDABLE" },
    { label: "Non-Refundable", value: "NON-REFUNDABLE" },
  ];

  // Values match the wallet_ledger columns the backend filters on:
  // transaction_type = PAYMENT/REFUND, payment_status = PAID/OVERDUE/REFUND.
  const transactionTypeOptions = [
    { label: "Payment", value: "PAYMENT" },
    { label: "Refund", value: "REFUND" },
  ];

  const transactionStatusOptions = [
    { label: "Paid", value: "PAID" },
    { label: "Overdue", value: "OVERDUE" },
    { label: "Refunded", value: "REFUND" },
  ];

  return (
    <div className="bookings-filters">
      <div className="bookings-filters__search">
        <img src={searchIcon} alt="Search" className="search-icon" />
        <input
          type="text"
          placeholder="Search"
          className="search-input"
          value={searchValue || ""}
          onChange={(e) => onSearch && onSearch(e.target.value)}
        />
      </div>

      <div className="bookings-filters__actions">
        <button
          className={`filter-button ${showFilters ? "filter-button--active" : ""}`}
          onClick={handleFilterToggle}
        >
          <img src={filterIcon} alt="Filter" />
          Filters
        </button>

        {onViewToggle && (
          <button className="view-disputes-button" onClick={onViewToggle}>
            {viewToggleLabel || "View Disputes"}
          </button>
        )}

        <button className="download-button" onClick={onDownload}>
          <img src={downloadIcon} alt="Download" />
          Download
        </button>
      </div>

      {showFilters && (
        <div className="bookings-filters__row">
          <div className="bookings-filters__dropdowns">
            <div className="filter-dropdown">
              <SelectPicker
                data={bookingStatusOptions}
                value={bookingStatus}
                onChange={handleBookingStatusChange}
                placeholder="Booking Status"
                searchable={false}
                cleanable={true}
                block
                className="filter-select-picker"
              />
            </div>
            <div className="filter-dropdown">
              <SelectPicker
                data={bookingTypeOptions}
                value={bookingType}
                onChange={handleBookingTypeChange}
                placeholder="Booking Type"
                searchable={false}
                cleanable={true}
                block
                className="filter-select-picker"
              />
            </div>
            <div className="filter-dropdown">
              <SelectPicker
                data={transactionTypeOptions}
                value={transactionType}
                onChange={handleTransactionTypeChange}
                placeholder="Transaction Type"
                searchable={false}
                cleanable={true}
                block
                className="filter-select-picker"
              />
            </div>
            <div className="filter-dropdown">
              <SelectPicker
                data={transactionStatusOptions}
                value={transactionStatus}
                onChange={handleTransactionStatusChange}
                placeholder="Transaction Status"
                searchable={false}
                cleanable={true}
                block
                className="filter-select-picker"
              />
            </div>
          </div>
          <div className="bookings-filters__date-ranges">
            <div className="date-range-wrapper">
              <div className="date-range-wrapper__label"></div>
              <DateRangePicker
                block
                value={getDateRangeValue("checkIn")}
                placeholder="Check-In Date Range"
                onChange={(value) => handleDateRangeChange("checkIn", value)}
                format="dd-MM-yyyy"
                renderValue={formatDateRange}
                className="date-range-picker"
              />
            </div>
            <div className="date-range-wrapper">
              <div className="date-range-wrapper__label"></div>
              <DateRangePicker
                block
                value={getDateRangeValue("payment")}
                placeholder="Transaction Date Range"
                onChange={(value) => handleDateRangeChange("payment", value)}
                format="dd-MM-yyyy"
                renderValue={formatDateRange}
                className="date-range-picker"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsTableFilters;
